import React, { FunctionComponent, useState } from "react";

import { ButtonBackgroundColor, FlexShrink, IconButton, Layout } from "@multiweb/reactComponents";
import { bemClassNames } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { EditDialogType } from "../../enums";
import { LOC_DUPLICATE } from "../../locKeys";
import { EditDialogState, WorkflowEditorProps } from "../../types";
import { UiWorkflow } from "../../UiWorkflow";
import { DuplicateActivityDialog } from "../dialogs/basicDialogs/DuplicateActivityDialog";
import { ActivityEditDialog, SetEditDialog, TransitionEditDialog } from "../dialogs/editDialogs";
import { AddActivityDropdownSelect } from "./AddActivityDropdownSelect";
import { DividerPosition, ToolbarSection } from "./components";
import { ActivitySearchSection, ObjectHandlingSection, PaintingHandlingSection } from "./sections";
import { SetSettingsDropdownSelect } from "./SetSettingsDropdownSelect";

export type ToolbarPanelComponentProps = {
    readonly selectedComponents: readonly number[];
    readonly selectedLinks: readonly number[];
    readonly isWorkflowReadOnly: boolean;
    readonly isOnlyStartActivitySelected: boolean;
    readonly saveWorkflow: () => Promise<boolean>;
    readonly onReloadClick: () => void;
    readonly createTransition: () => void;
    readonly deleteSelectedWfEntities: () => void;
    readonly deleteSet: () => void;
    readonly uiWorkflow: UiWorkflow;
    readonly connectText: string;
    readonly editText: string;
    readonly duplicateText: string;
    readonly deleteText: string;
} & WorkflowEditorProps;

const classNames = bemClassNames("wf-toolbar");
const wfToolbarPanelStyle = classNames();

export const ToolbarPanelComponent: FunctionComponent<ToolbarPanelComponentProps> = ({
    hostKey,
    selectedComponents,
    selectedLinks,
    isWorkflowReadOnly,
    isOnlyStartActivitySelected,
    saveWorkflow,
    onReloadClick,
    createTransition,
    deleteSelectedWfEntities,
    deleteSet,
    uiWorkflow,
    connectText,
    editText,
    duplicateText,
    deleteText,
}) => {
    const [editDialogState, setEditDialogState] = useState<EditDialogState>({ isOpen: false, type: null });
    const [showDuplicateActDiag, setShowDuplicateActDiag] = useState<boolean>(false);

    const isSomeWfElementSelected = selectedComponents.length > 0 || selectedLinks.length > 0;

    const openSetEditDialog = () => {
        setEditDialogState({ isOpen: true, type: EditDialogType.Set });
    };

    const onCloseEditDialog = () => {
        setEditDialogState({ isOpen: false, type: null });
    };

    const handleEditButtonClick = () => {
        if (selectedComponents.length >= 1)
            setEditDialogState({ isOpen: true, type: EditDialogType.Activity });
        else if (selectedLinks.length >= 1)
            setEditDialogState({ isOpen: true, type: EditDialogType.Transition });
    };

    const editDialogRenderer = () => {
        if (editDialogState.type === EditDialogType.Activity)
            return <ActivityEditDialog hostKey={hostKey} picassoIndex={selectedComponents[0]} onCloseEdit={onCloseEditDialog} />;
        if (editDialogState.type === EditDialogType.Transition)
            return <TransitionEditDialog hostKey={hostKey} picassoIndex={selectedLinks[0]} onCloseEdit={onCloseEditDialog} />;
        return <SetEditDialog hostKey={hostKey} onCloseEdit={onCloseEditDialog} />;
    };

    return (
        <div className={`standardMenu DesktopMenu ${wfToolbarPanelStyle}`} >
            <Layout flexShrink={FlexShrink.zero}>
                {!isWorkflowReadOnly && <ObjectHandlingSection {...{ hostKey, onReloadClick, saveWorkflow }} />}
                <PaintingHandlingSection {...{ hostKey }} />
                {!isWorkflowReadOnly && <ToolbarSection>
                    <AddActivityDropdownSelect {...{ hostKey, uiWorkflow }} />
                    <IconButton iconCode={IconCode.delete} disabled={!isSomeWfElementSelected} onClick={deleteSelectedWfEntities} outline={false} title={deleteText} backgroundColor={ButtonBackgroundColor.transparent} />
                    <IconButton iconCode={IconCode.copy} disabled={!selectedComponents.length || isOnlyStartActivitySelected} outline={false} onClick={() => setShowDuplicateActDiag(true)} title={duplicateText} backgroundColor={ButtonBackgroundColor.transparent} />
                    <IconButton iconCode={IconCode.editorialPencil} disabled={!isSomeWfElementSelected} onClick={handleEditButtonClick} outline={false} title={editText} backgroundColor={ButtonBackgroundColor.transparent} />
                    <IconButton iconCode={IconCode.uiCreateConnection} disabled={!selectedComponents.length} onClick={createTransition} outline={false} title={connectText} backgroundColor={ButtonBackgroundColor.transparent} />
                </ToolbarSection>}
                <ToolbarSection dividerPosition={DividerPosition.none}>
                    {!isWorkflowReadOnly && <Layout><SetSettingsDropdownSelect {...{ openSetEditDialog, deleteSet }} /></Layout>}
                </ToolbarSection>
            </Layout>
            <Layout>
                <ActivitySearchSection {...{ hostKey, uiWorkflow }} />
            </Layout>
            {editDialogState.isOpen && editDialogRenderer()}
            {showDuplicateActDiag && <DuplicateActivityDialog duplicateTextLocKey={LOC_DUPLICATE} onCancelClick={() => setShowDuplicateActDiag(false)} {...{ hostKey, uiWorkflow }} />}
        </div>
    );
};
